import { useDaily, useWaitingParticipants } from "./callTransport"
import { LockOpen, UserRound } from "lucide-react"
import { Button } from "components/ui/button"
import type { CallSurfaceWaitRoom } from "../types"

type WaitingRoomHostPanelProps = {
  waitRoom: CallSurfaceWaitRoom
  onAdmitted?: (participant: { id: string; name?: string }) => void
  /** Server admission has its own list; an empty Daily lobby is not a global empty state. */
  showEmptyState?: boolean
}

/**
 * Host-side waiting room: admit or deny knocking attendees (Daily) and unlock the meeting
 * for future authorized joiners (WiseFriend API, shared with the legacy renderer).
 */
export function WaitingRoomHostPanel({ waitRoom, onAdmitted, showEmptyState = true }: WaitingRoomHostPanelProps) {
  const daily = useDaily()
  const { waitingParticipants, grantAccess, denyAccess } = useWaitingParticipants()

  const admit = (id: string, name?: string) => {
    grantAccess(id)
    onAdmitted?.({ id, name })
  }

  const admitAll = () => {
    waitingParticipants.forEach((participant: any) => onAdmitted?.({ id: participant.id, name: participant.name }))
    grantAccess("*")
  }

  return (
    <section data-testid="call-waiting-room-host" className="space-y-3">
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-sm font-semibold text-white">Waiting room</h3>
        {waitingParticipants.length > 1 && (
          <Button type="button" size="auto" onClick={admitAll} className="h-8 px-3 text-xs">
            Admit all
          </Button>
        )}
      </div>
      {waitingParticipants.length === 0 ? (
        showEmptyState && <p className="text-xs text-white/60">Nobody is waiting right now.</p>
      ) : (
        <ul className="space-y-2">
          {waitingParticipants.map((participant: any) => (
            <li key={participant.id} data-testid={`call-waiting-participant-${participant.id}`} className="flex items-center gap-2 rounded-lg bg-white/5 px-3 py-2">
              <UserRound className="h-4 w-4 shrink-0 text-white/60" />
              <span className="min-w-0 flex-1 truncate text-sm text-white">{participant.name || "Guest"}</span>
              <Button type="button" size="auto" onClick={() => admit(participant.id, participant.name)} className="h-8 px-3 text-xs">
                Admit
              </Button>
              <Button type="button" size="auto" variant="secondary" onClick={() => denyAccess(participant.id)} className="h-8 px-3 text-xs">
                Deny
              </Button>
            </li>
          ))}
        </ul>
      )}
      {waitRoom.canUnlock && (
        <div className="rounded-lg border border-white/10 p-3">
          <p className="flex items-center gap-2 text-sm font-semibold text-white">
            <LockOpen className="h-4 w-4 text-[#f6a35c]" />
            Open the meeting
          </p>
          <p className="mt-1 text-xs text-white/60">Let future authorized joiners enter without requesting access.</p>
          <Button
            type="button"
            size="auto"
            onClick={() => void waitRoom.unlock()}
            disabled={waitRoom.unlocking || !daily}
            className="mt-2 h-8 px-3 text-xs"
            data-testid="call-unlock-wait-room"
          >
            {waitRoom.unlocking ? "Unlocking..." : "Unlock meeting"}
          </Button>
          {waitRoom.unlockError && <p role="alert" className="mt-2 text-xs text-red-300">{waitRoom.unlockError}</p>}
        </div>
      )}
      {waitRoom.unlocked && (
        <p className="text-xs text-white/60">The meeting is open. Authorized joiners no longer need to knock.</p>
      )}
    </section>
  )
}
